import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Search, Phone, Mail, Users, Download } from "lucide-react";

const fmtPhone = (p) => {
  const d = (p || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === "1") return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p || "";
};

export default function PlayerDirectory() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    api.get(`/admin/players/directory?q=${encodeURIComponent(dq)}`).then(({ data }) => setData(data)).catch(() => setData({ players: [], total: 0, with_phone: 0 }));
  }, [dq]);
  const rows = data?.players || [];
  const missing = useMemo(() => rows.filter((p) => !p.phone).length, [rows]);

  const exportCsv = () => {
    const lines = [["Name", "Phone", "Email", "Membership"].join(","), ...rows.map((p) => [p.name, p.phone, p.email, p.membership].map((v) => `"${(v || "").toString().replace(/"/g, '""')}"`).join(","))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `players-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  return (
    <div className="space-y-4" data-testid="player-directory">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="player-directory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone number or email…"
            className="w-full bg-pb-elevated border border-pb-border pl-9 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500" data-testid="player-directory-stats">
          <Users className="inline w-3 h-3 mr-1 -mt-0.5" />{data?.total ?? "…"} players · <span className="text-lime-300">{data?.with_phone ?? "…"}</span> with phone
          {dq && <> · showing {rows.length}{missing ? ` (${missing} no phone)` : ""}</>}
        </div>
        <button data-testid="player-directory-export" onClick={exportCsv} disabled={!rows.length}
          className="flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-2.5 hover:border-zinc-400 disabled:opacity-40 transition-colors">
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
      </div>
      <div className="border border-pb-border overflow-hidden">
        <div className="grid grid-cols-[1fr_10rem] sm:grid-cols-[1.2fr_10rem_1.4fr] gap-3 px-4 py-2 bg-pb-elevated/60 text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
          <div>Player</div><div>Phone</div><div className="hidden sm:block">Email</div>
        </div>
        <div className="max-h-[520px] overflow-y-auto divide-y divide-pb-border/60">
          {!data ? <div className="p-6 text-sm text-zinc-500 animate-pulse">Loading…</div>
            : rows.length === 0 ? <div className="p-6 text-sm text-zinc-500" data-testid="player-directory-empty">No players match "{dq}".</div>
            : rows.map((p) => (
              <div key={p.id} className="grid grid-cols-[1fr_10rem] sm:grid-cols-[1.2fr_10rem_1.4fr] gap-3 px-4 py-2.5 items-center hover:bg-pb-elevated/40" data-testid={`player-row-${p.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate" data-testid={`player-name-${p.id}`}>{p.name}</div>
                  <div className="text-[9px] uppercase tracking-widest text-zinc-600">{p.membership}{p.role !== "player" ? ` · ${p.role}` : ""}</div>
                </div>
                <div className="text-xs font-mono" data-testid={`player-phone-${p.id}`}>
                  {p.phone ? <a href={`tel:${p.phone.replace(/[^\d+]/g, "")}`} className="text-lime-300 hover:underline inline-flex items-center gap-1"><Phone className="w-3 h-3" />{fmtPhone(p.phone)}</a>
                    : <span className={`text-[9px] uppercase tracking-widest ${p.phone_skipped ? "text-amber-400" : "text-zinc-600"}`}>{p.phone_skipped ? "Declined" : "Not provided"}</span>}
                </div>
                <div className="hidden sm:block text-xs text-zinc-400 truncate" data-testid={`player-email-${p.id}`}>
                  <a href={`mailto:${p.email}`} className="hover:text-white inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3 shrink-0" />{p.email}</a>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
